import React from 'react';
import { Box, Flex, IconButton, Modal, Tooltip } from 'pounceV2';
import { useTheme } from '@mui/material';

import useCopyToClipboardV3 from 'Hooks/useCopyToClipboardV3';
import { PRINT_SUPPRESS_ATTR } from 'Components/utils/printAIResponse';
import { extractTableTSV } from './tableUtils';

const FloatingTableButtons = ({
  onCopy,
  onExpand,
}: {
  onCopy: () => void;
  onExpand: () => void;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  return (
    <Flex
      className="ai-table-actions"
      backgroundColor={tokens.background.neutral.canvas.base}
      borderRadius="8px"
      position="absolute"
      right={-8}
      top={-8}
      p="1px"
      gap={1}
      {...{ [PRINT_SUPPRESS_ATTR]: true }}
    >
      <Tooltip content="Copy as TSV" placement="top">
        <IconButton
          variant="outline"
          size="small"
          icon="copy"
          aria-label="Copy table as TSV"
          onClick={e => {
            onCopy();
            e.currentTarget.blur();
          }}
        />
      </Tooltip>
      <Tooltip content="Expand" placement="top">
        <IconButton
          variant="outline"
          size="small"
          icon="expand"
          aria-label="Expand table"
          onClick={e => {
            onExpand();
            e.currentTarget.blur();
          }}
        />
      </Tooltip>
    </Flex>
  );
};

const TableModalButtons = ({
  onCopy,
  closeModal,
}: {
  onCopy: () => void;
  closeModal: () => void;
}) => (
  <Flex justifyContent="flex-end" p={2} gap={1} flexShrink={0} {...{ [PRINT_SUPPRESS_ATTR]: true }}>
    <Tooltip content="Copy as TSV" placement="top">
      <IconButton
        variant="outline"
        size="medium"
        icon="copy"
        aria-label="Copy table as TSV"
        onClick={onCopy}
      />
    </Tooltip>
    <Tooltip content="Collapse" placement="top">
      <IconButton
        variant="outline"
        size="medium"
        icon="collapse"
        aria-label="Collapse"
        onClick={closeModal}
      />
    </Tooltip>
  </Flex>
);

interface AITableV3Props {
  children?: React.ReactNode;
}

/**
 * Wraps AI-rendered markdown tables with a hover overlay (copy + expand buttons)
 * and a full-screen lightbox modal — mirroring the MermaidDiagramV3 pattern.
 *
 * Copy places tab-separated values on the clipboard, which paste correctly into
 * Excel, Google Sheets, and Notion.
 */
const AITableV3 = ({ children }: AITableV3Props) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const copyToClipboard = useCopyToClipboardV3();
  const {
    palette: { tokens },
  } = useTheme();

  const handleCopy = React.useCallback(() => {
    // tableRef targets the inline table, which stays mounted behind the modal.
    // Reading from it is correct even when the modal is open.
    if (!tableRef.current) {
      return;
    }
    const tsv = extractTableTSV(tableRef.current);
    copyToClipboard(tsv, {
      title: 'Table copied to clipboard!',
      duration: 1500,
      variant: 'success',
    });
  }, [copyToClipboard]);

  return (
    <Box
      data-testid="ai-table-container"
      position="relative"
      my={2}
      sx={{
        '& .ai-table-actions': { opacity: 0, transition: 'opacity 0.15s' },
        '&:hover .ai-table-actions': { opacity: 1 },
      }}
    >
      <Box overflowX="auto">
        <Box
          as="table"
          ref={tableRef}
          borderCollapse="collapse"
          width="100%"
          minWidth="max-content"
          borderColor={tokens.stroke.neutral[4]}
        >
          {children}
        </Box>
      </Box>

      <FloatingTableButtons onCopy={handleCopy} onExpand={() => setIsModalOpen(true)} />

      {/* overlayZIndex must exceed MUI Drawer z-index (1200) to render above parent containers */}
      <Modal
        fullScreen
        padded={false}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        overlayZIndex={1400}
      >
        {isModalOpen && (
          <Flex direction="column" width="100%" height="100%">
            <TableModalButtons onCopy={handleCopy} closeModal={() => setIsModalOpen(false)} />
            <Box data-testid="ai-table-modal-content" flex={1} overflow="auto" px={6} pb={6}>
              <Box
                as="table"
                borderCollapse="collapse"
                width="100%"
                borderColor={tokens.stroke.neutral[4]}
              >
                {children}
              </Box>
            </Box>
          </Flex>
        )}
      </Modal>
    </Box>
  );
};

export default React.memo(AITableV3);
